import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CustomerHeader from "@/components/CustomerHeader";
import { toast } from "sonner";
import { Copy, Trash2 } from "lucide-react";

const ZODIACS = ["Koç", "Boğa", "İkizler", "Yengeç", "Aslan", "Başak", "Terazi", "Akrep", "Yay", "Oğlak", "Kova", "Balık"];

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [birth, setBirth] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", { name, birth_date: birth || undefined });
      setUser(data);
      toast.success("Profil güncellendi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncellenemedi");
    } finally { setSaving(false); }
  };

  const copyRef = () => {
    const link = `${window.location.origin}/kayit?ref=${user?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Davet linki kopyalandı");
  };

  const deleteAccount = async () => {
    const confirm1 = window.prompt('Hesabını kalıcı silmek üzeresin. TÜM verilerin (fal geçmişin, jetonların, üyeliğin) silinecek. Devam etmek için "SIL" yaz:');
    if (confirm1 !== "SIL") return;
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      toast.success("Hesabın silindi.");
      logout();
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    } finally { setDeleting(false); }
  };

  return (
    <div className="cosmic-bg min-h-screen">
      <div className="stars" />
      <CustomerHeader />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-serif-myst text-4xl mb-6">Profilim</h1>

        <div className="glass rounded-2xl p-6 mb-4">
          <div className="text-xs uppercase tracking-wider text-white/50 mb-3">Bilgilerim</div>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-white/60 mb-1 block">Ad</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]/50" />
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1 block">E-posta</label>
              <input value={user?.email || ""} disabled className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/50" />
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1 block">Doğum Tarihi (burcunu belirler)</label>
              <input type="date" value={birth} onChange={(e) => setBirth(e.target.value)} data-testid="profile-birth-input" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]/50" />
              {user?.zodiac_sign && <div className="text-xs gold-text mt-1">Mevcut burcun: {user.zodiac_sign}</div>}
            </div>
            <button onClick={save} disabled={saving} data-testid="profile-save-btn" className="btn-gold px-6 py-2.5 rounded-full">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="text-xs uppercase tracking-wider text-white/50 mb-3">Arkadaş Davet Et</div>
          <p className="text-sm text-white/70 mb-3">Her arkadaşın için <span className="gold-text font-semibold">25 jeton</span> hediye kazan.</p>
          <div className="flex gap-2">
            <input readOnly value={`${window.location.origin}/kayit?ref=${user?.referral_code}`} className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white/80 text-sm" />
            <button onClick={copyRef} data-testid="copy-referral-btn" className="btn-ghost px-4 rounded-xl flex items-center gap-1">
              <Copy className="w-4 h-4" /> Kopyala
            </button>
          </div>
        </div>

        <div className="mt-8 border border-red-500/20 bg-red-500/5 rounded-2xl p-6">
          <div className="text-xs uppercase tracking-wider text-red-300 mb-3">Tehlikeli Bölge</div>
          <p className="text-sm text-white/70 mb-4">
            Hesabını kalıcı olarak sil. Tüm fal geçmişin, jetonların, üyeliğin ve verilerin
            silinecek. Bu işlem <strong>geri alınamaz</strong>.
          </p>
          <button onClick={deleteAccount} disabled={deleting} data-testid="delete-account-btn" className="border border-red-500/40 text-red-300 hover:bg-red-500/10 px-5 py-2 rounded-full text-sm flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> {deleting ? "Siliniyor..." : "Hesabımı Sil"}
          </button>
        </div>
      </main>
    </div>
  );
}
